import * as React from 'react';
import { CopyToClipboard } from 'react-copy-to-clipboard';

import {
  Input,
  Snackbar,
  defaultSnackbarTimeout,
  ACTIVITY_IDS_ARRAY,
  CLASSROOMS,
  UNIT_ID,
  UNIT_NAME,
  UNIT_TEMPLATE_ID,
  UNIT_TEMPLATE_NAME,
} from '../../../../Shared/index';
import useSnackbarMonitor from '../../../../Shared/hooks/useSnackbarMonitor';
import AssignmentFlowNavigation from '../assignment_flow_navigation';
import ScrollToTop from '../../shared/scroll_to_top';

interface UnitAssignmentFollowupProps {
  referralCode: string;
}

const emailSrc = `${window.postbuildEnv.CDN_URL}/images/icons/ui-share-email.svg`;
const facebookSrc = `${window.postbuildEnv.CDN_URL}/images/icons/ui-share-facebook.svg`;
const giftSrc = `${window.postbuildEnv.CDN_URL}/images/illustrations/gift.svg`;
const googleSrc = `${window.postbuildEnv.CDN_URL}/images/icons/ui-share-google.svg`;
const twitterSrc = `${window.postbuildEnv.CDN_URL}/images/icons/ui-share-twitter.svg`;

const UnitAssignmentFollowup: React.FC<UnitAssignmentFollowupProps> = ({
  referralCode,
}) => {
  const [leaving, setLeaving] = React.useState(false);
  const [showSnackbar, setShowSnackbar] = React.useState(false);
  const [snackbarCopy, setSnackbarCopy] = React.useState('');

  useSnackbarMonitor(showSnackbar, setShowSnackbar, defaultSnackbarTimeout);

  const prepareToLeavePage = React.useCallback((): Promise<void> => {
    return new Promise((resolve) => {
      setLeaving(true);
      window.localStorage.removeItem(UNIT_ID);
      window.localStorage.removeItem(UNIT_NAME);
      window.localStorage.removeItem(UNIT_TEMPLATE_ID);
      window.localStorage.removeItem(UNIT_TEMPLATE_NAME);
      window.localStorage.removeItem(ACTIVITY_IDS_ARRAY);
      window.localStorage.removeItem(CLASSROOMS);
      resolve();
    });
  }, []);

  React.useEffect(() => {
    return () => {
      prepareToLeavePage();
    };
  }, [prepareToLeavePage]);

  const handleClickToDashboard = () => {
    prepareToLeavePage().then(() => {
      window.location.href = window.postbuildEnv.DEFAULT_URL;
    });
  };

  const handleCopyLink = () => {
    setShowSnackbar(true);
    setSnackbarCopy('Link copied');
  };

  const renderReferral = () => {
    const referralLink =
      `${window.postbuildEnv.DEFAULT_URL}/?referral_code=${referralCode}`;
    return (
      <div className="unit-assignment-followup referral">
        <Snackbar text={snackbarCopy} visible={showSnackbar} />
        <div className="referral-card">
          <img alt="gift" src={giftSrc} />
          <h1>
            Recommend Quill and earn a month of free Quill Premium for Teachers.
          </h1>
          <p>
            As a nonprofit organization that provides free activities, Quill relies on teachers to share the word.
          </p>
          <p>
            For every teacher that signs up for Quill with your referral link and completes an activity with their students, you earn a month of free Quill Premium, and they receive a one-month free trial.
          </p>
          <div className="share-box">
            <div className="referral-link-container">
              <Input ariaLabel="Referral link" disabled id="referral-link" value={referralLink} />
              <CopyToClipboard onCopy={handleCopyLink} text={referralLink}>
                <button className="quill-button-archived secondary outlined small" type="button">
                  Copy link
                </button>
              </CopyToClipboard>
            </div>
            <p className="share-text">More ways to share: </p>
            <div className="share-links">
              <a
                href={`https://x.com/home?status=I'm using @quill_org to help my students become better writers and critical thinkers. Want to join me? ${referralLink}`}
                rel="noopener noreferrer"
                target="_blank"
              >
                <img alt="twitter icon" src={twitterSrc} />
              </a>
              <a
                href={`https://www.facebook.com/share.php?u=${referralLink}`}
                rel="noopener noreferrer"
                target="_blank"
              >
                <img alt="facebook icon" src={facebookSrc} />
              </a>
              <a
                href={`https://plus.google.com/share?url=${referralLink}`}
                rel="noopener noreferrer"
                target="_blank"
              >
                <img alt="google plus icon" src={googleSrc} />
              </a>
              <a
                href={`mailto:?subject=Free tool to help your students become better writers&body=Hi! I've been using this free tool called Quill.org to help your students become better writers and critical thinkers, and I wanted to let you know about it. Hopefully it helps your students as much as it's helped mine! ${referralLink}`}
                rel="noopener noreferrer"
                target="_blank"
              >
                <img alt="mail" src={emailSrc} />
              </a>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderFollowUp = () => {
    if (leaving) return null;
    return renderReferral();
  };

  return (
    <React.Fragment>
      <AssignmentFlowNavigation
        button={
          <button className="quill-button medium contained green focus-on-light" onClick={handleClickToDashboard} type="button">
            Take me to my dashboard
          </button>
        }
      />
      <ScrollToTop />
      <div className="container">{renderFollowUp()}</div>
    </React.Fragment>
  );
};

export default UnitAssignmentFollowup;
